import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
        credentials: 'include'
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-white">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">Dashboard</h1>
          <p className="text-gray-400">Welcome to your admin portal</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            title="Total Costings"
            value={stats?.total_costings || 0}
            icon="📊"
            color="blue"
          />
          <StatCard
            title="Draft Costings"
            value={stats?.draft_costings || 0}
            icon="📝"
            color="yellow"
          />
          <StatCard
            title="Completed Costings"
            value={stats?.completed_costings || 0}
            icon="✅"
            color="green"
          />
        </div>

        {/* Recent Costings */}
        <div className="bg-[#0F1629] border border-[#1E293B] rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-serif text-white">Recent Costings</h2>
            <Link
              to="/admin/costing"
              className="text-sm text-[#C4A962] hover:text-[#B89952] transition-colors"
            >
              View All →
            </Link>
          </div>

          {stats?.recent_costings && stats.recent_costings.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_costings.map((costing) => (
                <Link
                  key={costing.id}
                  to={`/admin/costing/${costing.id}`}
                  className="block p-4 bg-[#0A0F1E] border border-[#1E293B] rounded-lg hover:border-[#C4A962] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">{costing.reference}</div>
                      <div className="text-sm text-gray-400 mt-1">
                        {costing.client_name} • {costing.destination}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        costing.status === 'Draft' ? 'bg-yellow-500/10 text-yellow-400' :
                        costing.status === 'Completed' ? 'bg-green-500/10 text-green-400' :
                        'bg-gray-500/10 text-gray-400'
                      }`}>
                        {costing.status}
                      </span>
                      <span className="text-gray-500">→</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p className="mb-4">No costings yet</p>
              <Link
                to="/admin/costing/new"
                className="inline-block px-6 py-3 bg-[#C4A962] hover:bg-[#B89952] text-[#0A0F1E] rounded-lg transition-colors"
              >
                Create First Costing
              </Link>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ title, value, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    green: 'bg-green-500/10 text-green-400'
  };

  return (
    <div className="bg-[#0F1629] border border-[#1E293B] rounded-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-2">{title}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
        </div>
        <div className={`text-4xl ${colorClasses[color]} p-3 rounded-lg`}>
          {icon}
        </div>
      </div>
    </div>
  );
}
